import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle, XCircle, Banknote, Edit } from 'lucide-react'
import { getExpense, approveExpense, rejectExpense, reimburseExpense } from '../services/api'
import { formatCurrency, formatDate, statusBadgeClass } from '../lib/formatters'

export default function ExpenseDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [expense, setExpense] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState('')

  const load = () => {
    setLoading(true)
    getExpense(id).then((res) => setExpense(res.data)).catch(() => navigate('/expenses')).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [id])

  const action = async (fn, ...args) => {
    setActionLoading(fn.name)
    try { await fn(...args); load() }
    catch (err) { alert(err.response?.data?.error || 'Action failed') }
    finally { setActionLoading('') }
  }

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-8 w-48 bg-gray-200 rounded" /><div className="h-40 bg-gray-100 rounded" /></div>
  if (!expense) return null

  const exp = expense

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/expenses" className="p-1.5 text-gray-400 hover:text-gray-600"><ArrowLeft className="h-5 w-5" /></Link>
        <h1 className="text-2xl font-semibold text-gray-900">Expense</h1>
        <span className={`badge ${statusBadgeClass(exp.status)}`}>{exp.status}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {exp.status === 'SUBMITTED' && (
          <>
            <Link to={`/expenses/${id}/edit`} className="btn-secondary flex items-center gap-1.5"><Edit className="h-4 w-4" /> Edit</Link>
            <button onClick={() => action(approveExpense, id)} disabled={!!actionLoading} className="btn-primary flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Approve
            </button>
            <button onClick={() => { const notes = prompt('Rejection reason:'); if (notes) action(rejectExpense, id, notes) }} disabled={!!actionLoading} className="btn-danger flex items-center gap-1.5">
              <XCircle className="h-4 w-4" /> Reject
            </button>
          </>
        )}
        {exp.status === 'APPROVED' && (
          <button onClick={() => { if (confirm('Mark as reimbursed?')) action(reimburseExpense, id) }} disabled={!!actionLoading} className="btn-primary flex items-center gap-1.5">
            <Banknote className="h-4 w-4" /> Mark Reimbursed
          </button>
        )}
      </div>

      <div className="card grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
        <div><span className="text-gray-500 block">Date</span>{formatDate(exp.date)}</div>
        <div><span className="text-gray-500 block">Amount</span><span className="font-semibold">{formatCurrency(exp.amount)}</span></div>
        <div><span className="text-gray-500 block">Category</span>{exp.category?.name || '—'}</div>
        <div><span className="text-gray-500 block">Submitted By</span>{exp.paidBy?.name || exp.submittedBy?.name || '—'}</div>
        <div><span className="text-gray-500 block">Vendor</span>{exp.vendor || '—'}</div>
        <div><span className="text-gray-500 block">Reference</span>{exp.referenceNo || '—'}</div>
        {exp.approvedBy && <div><span className="text-gray-500 block">Approved By</span>{exp.approvedBy.name}</div>}
        {exp.reimbursedDate && <div><span className="text-gray-500 block">Reimbursed</span>{formatDate(exp.reimbursedDate)}</div>}
      </div>

      <div className="card">
        <h2 className="text-sm font-medium text-gray-500 mb-1">Description</h2>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{exp.description}</p>
      </div>

      {exp.rejectionNotes && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
          <strong className="text-red-700">Rejection Reason:</strong> <span className="text-red-600">{exp.rejectionNotes}</span>
        </div>
      )}

      {exp.notes && <div className="card"><h2 className="text-sm font-medium text-gray-500 mb-1">Notes</h2><p className="text-sm text-gray-700 whitespace-pre-wrap">{exp.notes}</p></div>}
    </div>
  )
}
